/**
 * My Profile — account, plan, sign out.
 *
 * Replaces the old "everything on /profiles" model. Plan info and Sign out
 * live here.
 *
 * No tile/board editing happens on this page — that's all on the
 * board-scoped top-nav tabs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  User,
  CreditCard,
  LogOut,
  ShieldCheck,
  Tablet,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";

export default function MyProfile() {
  useSEO({
    title: "My profile",
    description: "Your account, plan, and sign out.",
    path: "/my-profile",
  });
  const { user, logout } = useAuth();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const planLabel =
    billing?.access?.status === "sponsored" ? "Sponsored pass"
      : billing?.active ? (billing?.tier || "Active")
      : "Early access";

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      <div className="max-w-3xl mx-auto">
        <h1
          className="display text-2xl sm:text-3xl font-extrabold mb-2 flex items-center gap-2"
          data-testid="my-profile-title"
        >
          <User size={24} aria-hidden /> My profile
        </h1>
        <p className="text-sm text-[color:var(--ink)]/70 mb-5">
          Your account, plan, and a few helpful shortcuts.
        </p>

        {/* Account card */}
        <section className="brutal-card bg-white p-5 mb-4" data-testid="my-profile-account">
          <h2 className="text-lg font-extrabold mb-1">Account</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-2 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">Name</div>
              <div className="font-bold">{user?.name || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--ink)]/60">Email</div>
              <div className="font-bold break-all">{user?.email || "—"}</div>
            </div>
          </div>
        </section>

        {/* Plan card */}
        <section className="brutal-card bg-white p-5 mb-4" data-testid="my-profile-plan">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} aria-hidden />
            <h2 className="text-lg font-extrabold">Plan</h2>
          </div>
          <div className="text-sm mb-3">
            <span className="px-2 py-1 rounded-full bg-[color:var(--gold)] font-bold text-xs">
              {planLabel}
            </span>
            {billing?.sponsored_until && (
              <span className="ml-2 text-xs text-[color:var(--ink)]/70">
                until {new Date(billing.sponsored_until).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/pricing" className="pill-btn" data-testid="my-profile-plan-pricing">
              See plans & pricing
            </Link>
            <Link to="/privacy/me" className="pill-btn" data-testid="my-profile-privacy">
              <ShieldCheck size={14} /> Privacy & data
            </Link>
            <Link to="/admin/child-devices" className="pill-btn" data-testid="my-profile-tablets">
              <Tablet size={14} /> Child tablets
            </Link>
          </div>
        </section>

        {/* Sign out card */}
        <section className="brutal-card bg-white p-5">
          <button
            type="button"
            onClick={logout}
            className="pill-btn"
            data-testid="my-profile-signout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </section>
      </div>
    </div>
  );
}
